import React, { useState, useEffect, useCallback } from 'react';
import { Ruler } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Table, Column } from '../../components/ui/Table';
import { PropertySelect } from '../../components/ui/PropertySelect';
import { api } from '../../services/api';
import { ACCESS_STATS_PRICE_M2, usePermissions } from '../../hooks/usePermissions';
import type { Property } from '../../types';

type RentPerM2Row = {
  id: string;
  name: string;
  city: string;
  m2Total: number;
  totalMonthlyRent: number;
  rentPerM2: number;
};

type UnitBreakdownRow = {
  id: string;
  unitNumber: string;
  m2: number;
  tenantName: string | null;
  monthlyRent: number | null;
  rentPerM2: number | null;
  status: 'occupied' | 'available';
};

function propertiesToSelectOptions(rows: RentPerM2Row[]): Property[] {
  return rows.map((p) => ({
    id: p.id,
    name: p.name,
    address: '',
    city: p.city || '',
    state: '',
    unitCount: 0,
    occupiedUnits: 0,
    m2Total: p.m2Total,
    pricePerM2: 0,
    totalValue: 0,
    status: 'operativo',
    createdAt: '',
  }));
}

export const PricePerM2: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{
    averageRentPerM2: number;
    maxRentPerM2: number;
    minRentPerM2: number;
  } | null>(null);
  const [properties, setProperties] = useState<RentPerM2Row[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [unitsPayload, setUnitsPayload] = useState<{
    summary: { totalUnits: number; occupiedCount: number; availableCount: number; averageRentPerM2: number };
    units: UnitBreakdownRow[];
  } | null>(null);

  const { hasPermission, hasAnyPermission } = usePermissions();
  /** En esta pantalla el permiso de estadística Valor m² implica ver montos (el backend ya devuelve datos). */
  const canViewPrices =
    hasPermission('view_prices') || hasAnyPermission([...ACCESS_STATS_PRICE_M2]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getRentPerM2Stats();
      setSummary(data.summary);
      setProperties(data.properties || []);
    } catch (err: any) {
      setError(err.message || 'Error al cargar valor m²');
      setSummary(null);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedPropertyId) {
      setUnitsPayload(null);
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setUnitsLoading(true);
        setUnitsError(null);
        const res = await api.getRentPerM2Units(selectedPropertyId);
        if (!cancelled) {
          setUnitsPayload({ summary: res.summary, units: res.units });
        }
      } catch (err: any) {
        if (!cancelled) {
          setUnitsError(err.message || 'Error al cargar unidades');
          setUnitsPayload(null);
        }
      } finally {
        if (!cancelled) setUnitsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPropertyId]);

  const toNum = (v: unknown): number => (v === null || v === undefined ? 0 : Number(v) || 0);
  const formatM2 = (value: number) => `${toNum(value).toFixed(2)} m²`;
  const averageRentPerM2 = summary ? summary.averageRentPerM2 : 0;
  const maxRentPerM2 = summary ? summary.maxRentPerM2 : 0;
  const minRentPerM2 = summary ? summary.minRentPerM2 : 0;

  const formatCurrency = (value: number) =>
    value.toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const unitColumns: Column<UnitBreakdownRow>[] = [
    { header: 'Unidad', accessor: 'unitNumber', sortable: true },
    {
      header: 'm²',
      accessor: (row) => formatM2(row.m2),
      sortable: true,
    },
    {
      header: 'Inquilino',
      accessor: (row) => row.tenantName || '—',
    },
    {
      header: 'Renta mensual',
      accessor: (row) =>
        row.status === 'available' || row.monthlyRent == null
          ? '—'
          : canViewPrices
            ? formatCurrency(toNum(row.monthlyRent))
            : '—',
    },
    {
      header: 'Precio/m²',
      accessor: (row) =>
        row.rentPerM2 == null || row.rentPerM2 <= 0
          ? '—'
          : canViewPrices
            ? formatCurrency(toNum(row.rentPerM2))
            : '—',
      sortable: true,
    },
    {
      header: 'Estado',
      accessor: (row) =>
        row.status === 'occupied' ? (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Ocupada</span>
        ) : (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Disponible</span>
        ),
    },
  ];

  const columns: Column<RentPerM2Row>[] = [
    { header: 'Propiedad', accessor: 'name', sortable: true },
    { header: 'Ciudad', accessor: 'city', sortable: true },
    {
      header: 'm² Total',
      accessor: (row) => formatM2(row.m2Total),
      sortable: true,
    },
    {
      header: 'Renta por m²',
      accessor: (row) => (canViewPrices ? formatCurrency(toNum(row.rentPerM2)) : '—'),
      sortable: true,
    },
    {
      header: 'Renta mensual total',
      accessor: (row) => (canViewPrices ? formatCurrency(toNum(row.totalMonthlyRent)) : '—'),
      sortable: true,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <Ruler className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Valor m²</h1>
        </div>
        <div className="flex justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <Ruler className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Valor m²</h1>
        </div>
        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm font-medium"
        >
          Actualizar
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-sm text-gray-600 mb-1">Renta promedio por m²</div>
          <div className="text-2xl font-bold text-primary">{canViewPrices ? formatCurrency(averageRentPerM2) : '—'}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Renta máxima por m²</div>
          <div className="text-2xl font-bold text-green-600">{canViewPrices ? formatCurrency(maxRentPerM2) : '—'}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Renta mínima por m²</div>
          <div className="text-2xl font-bold text-red-600">{canViewPrices ? formatCurrency(minRentPerM2) : '—'}</div>
        </Card>
      </div>

      <Card title="Detalle por Propiedad">
        {properties.length === 0 ? (
          <p className="text-gray-500 py-4">No hay propiedades.</p>
        ) : (
          <Table data={properties} columns={columns} />
        )}
      </Card>

      <Card title="Desglose por unidad (departamento)">
        <p className="text-sm text-gray-600 mb-4">
          Selecciona una propiedad para ver cada unidad, la renta del inquilino activo y el precio por m² de esa renta.
        </p>
        <div className="max-w-xl mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Propiedad</label>
          <PropertySelect
            properties={propertiesToSelectOptions(properties)}
            value={selectedPropertyId}
            onChange={setSelectedPropertyId}
            placeholder="Selecciona una propiedad…"
          />
        </div>

        {unitsError && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4">{unitsError}</div>
        )}

        {selectedPropertyId && unitsLoading && (
          <div className="flex justify-center py-10">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}

        {selectedPropertyId && !unitsLoading && unitsPayload && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-xs text-gray-600">Unidades</div>
                <div className="text-lg font-semibold text-gray-900">{unitsPayload.summary.totalUnits}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-xs text-gray-600">Ocupadas</div>
                <div className="text-lg font-semibold text-green-700">{unitsPayload.summary.occupiedCount}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-xs text-gray-600">Disponibles</div>
                <div className="text-lg font-semibold text-gray-700">{unitsPayload.summary.availableCount}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-xs text-gray-600">Promedio precio/m² (con renta)</div>
                <div className="text-lg font-semibold text-primary">
                  {canViewPrices ? formatCurrency(toNum(unitsPayload.summary.averageRentPerM2)) : '—'}
                </div>
              </div>
            </div>
            {unitsPayload.units.length === 0 ? (
              <p className="text-gray-500 py-4">Esta propiedad no tiene unidades registradas.</p>
            ) : (
              <Table data={unitsPayload.units} columns={unitColumns} />
            )}
          </>
        )}
      </Card>
    </div>
  );
};
